<template>
  <div class="child-list-container">
    <h3>Список детей</h3>

    <div class="row g-3">
      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="iin" class="form-label col-md-3">ИИН ребенка</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="text" class="form-control" id="iin" v-model="searchParam.iin" />
            </div>
          </div>
        </div>
      </div>
      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="firstName" class="form-label col-md-3">Имя</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="text" class="form-control" id="firstName" v-model="searchParam.firstNameLike" />
            </div>
          </div>
        </div>
      </div>
      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="lastName" class="form-label col-md-3">Фамилия</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="text" class="form-control" id="lastName" v-model="searchParam.lastNameLike" />
            </div>
          </div>
        </div>
      </div>
      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="middleName" class="form-label col-md-3">Отчество</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="text" class="form-control" id="middleName" v-model="searchParam.middleNameLike" />
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="bin" class="form-label col-md-3">БИН</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="text" class="form-control" id="bin" v-model="searchParam.bin" />
            </div>
          </div>
        </div>
      </div>
      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="lmsId" class="form-label col-md-3">ID LMS</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="text" class="form-control" id="lmsId" v-model="searchParam.lmsId" />
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="durationTime" class="form-label col-md-3">Время прибывания</label>
          <div class="col-md-9">
            <div class="input-group">
              <multiselect
                  v-model="durationTime"
                  :options="durationTimeList"
                  :searchable="true"
                  :closeOnSelect="true"
                  track-by="code"
                  :custom-label="formattedDirectoryLabel"
                  placeholder="Выберите"
                  :multiple="false"
                  :clear-on-select="true"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="educationalType" class="form-label col-md-3">Образовательный вид</label>
          <div class="col-md-9">
            <div class="input-group">
              <multiselect
                  v-model="educationalType"
                  :options="educationalTypeList"
                  :searchable="true"
                  :closeOnSelect="true"
                  track-by="code"
                  :custom-label="formattedDirectoryLabel"
                  placeholder="Выберите"
                  :multiple="false"
                  :clear-on-select="true"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <button v-if="!isLoading" class="btn btn-primary" @click="fetchData(true)">Поиск</button>
          <button v-else class="btn btn-primary" disabled>Загрузка...</button>
          <button class="btn btn-warning" @click="resetFilter">Сбросить фильтр</button>
        </div>
      </div>
    </div>

    <b-pagination v-if="!isLoading" v-model="currentPage" :total-rows="pageResponse?.totalElements" :per-page="searchParam.perPage"></b-pagination>

    <b-table-simple bordered="">
      <b-thead head-variant="light">
        <b-tr>
          <b-th>#</b-th>
          <b-th>ИИН</b-th>
          <b-th>ФИО</b-th>
          <b-th>Время прибывания </b-th>
          <b-th>Образовательный вид</b-th>
          <b-th>БИН ДО </b-th>
          <b-th>ID LMS</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <template v-for="(document, docIndex) in childShortInfoList" :key="docIndex">
          <b-tr>
            <b-td>{{ ((currentPage-1) * searchParam.perPage) + docIndex + 1 }}</b-td>
            <b-td>
              <router-link :to="`/children/${document.childId}`" class="username-link">
                {{document.childIin}}
              </router-link>
            </b-td>
            <b-td>{{document.fio}}</b-td>
            <b-td>{{document.durationTimeName}}</b-td>
            <b-td>{{document.educationalTypeName}}</b-td>
            <b-td>{{document.bin}}</b-td>
            <b-td>{{document.lmsId}}</b-td>
          </b-tr>
        </template>
      </b-tbody>
    </b-table-simple>

  </div>
</template>

<script lang="ts">

import {defineComponent, onMounted, reactive, ref, watch} from "vue";
import Multiselect from "vue-multiselect";
import cacheService from "@/services/cacheService";
import {DurationTimeType, EducationalType } from "@/types/kindergarten";
import {SearchParam} from "@/types/searchParam";
import {PageResponse} from "@/types/pageResponse";
import {ChildrenShortInfoResponse} from "@/types/childrenResponse";
import {findChildrenByFilter} from "@/services/childrenService";

export default defineComponent({
  name: "DirectoryView",
  components: {Multiselect},
  setup() {
    const currentPage = ref(1);
    const durationTimeList = ref<DurationTimeType[]>([]);
    const durationTime = ref<DurationTimeType | null>(null);
    const educationalTypeList = ref<EducationalType[]>([]);
    const educationalType = ref<EducationalType | null>(null);
    const pageResponse = ref<PageResponse<ChildrenShortInfoResponse>>();
    const childShortInfoList = ref<ChildrenShortInfoResponse[]>([]);
    const isLoading = ref(false);
    const searchParam = reactive({
      perPage: 10,
      page: 1,
      iin: "",
      firstNameLike: "",
      lastNameLike: "",
      middleNameLike: "",
      bin: "",
      lmsId: "",
      durationTimeCode: "",
      educationalTypeCode: ""
    });

    const fetchCachedData = async() => {
      durationTimeList.value = await cacheService.useDurationTime().fetchDurationTime();
      durationTime.value = durationTimeList.value.find(item => item.code === 3) || null;
      educationalTypeList.value = await cacheService.useEducationalType().fetchEducationalType();
      educationalType.value = educationalTypeList.value.find(item => item.code === 1) || null;
    }

    onMounted(() => {
      fetchCachedData();
    });

    const formattedDirectoryLabel = (item: any) => {
      return `${item.code} | ${item.name}`;
    };

    const resetFilter = async () => {
      searchParam.iin = "";
      searchParam.firstNameLike = "";
      searchParam.lastNameLike = "";
      searchParam.middleNameLike = "";
      searchParam.bin = "";
      searchParam.lmsId = "";
    }

    const fetchData = async (isPageDefault: boolean = false) => {
      try {
        console.log(durationTime.value);
        if (durationTime.value && durationTime.value.code) {
          searchParam.durationTimeCode = durationTime.value ?  durationTime.value?.code + "" : "";
        }
        if (educationalType.value && educationalType.value.code) {
          searchParam.educationalTypeCode = educationalType.value ? educationalType?.value.code + "" : "";
        }
        console.log(searchParam);
        // if (searchParam.iin == "" && searchParam.firstNameLike == ""
        //     && searchParam.lastNameLike == "" && searchParam.middleNameLike == ""
        //     && searchParam.bin == "" && searchParam.lmsId == "") {
        //   alert("Выберите один из фильтров: ИИН, Имя, Фамилия, Отчество, БИН ДО, LMS ID");
        //   return;
        // }
        isLoading.value = true;
        const searchParamResponse = new SearchParam(searchParam.perPage, currentPage.value, searchParam, {});
        if (isPageDefault) {
          searchParamResponse.partNumber = 1;
          currentPage.value = 1;
        }
        pageResponse.value = await findChildrenByFilter(searchParamResponse);
        childShortInfoList.value = pageResponse.value.content;
        isLoading.value = false;
      } catch (error) {
        console.error("Error fetching child short info list:", error);
      }
    };

    watch(currentPage, (newPage) => {
      searchParam.page = newPage - 1;
      fetchData()
    });

    return {
      searchParam,
      durationTimeList,
      durationTime,
      educationalTypeList,
      educationalType,
      pageResponse,
      currentPage,
      childShortInfoList,
      isLoading,
      formattedDirectoryLabel,
      resetFilter,
      fetchData
    };
  }
});
</script>

<style scoped>
.child-list-container {
  padding: 20px;
  overflow:scroll;
}

h1 {
  margin-bottom: 20px;
}
</style>